import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Offer, PriceRequest } from 'rest-client';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AbsuidService } from '@app/modules/core/services/absuid/absuid.service';
import { GoogleTagManagerService } from '@app/modules/core/services/analytics/google-tag-manager.service';
import { OfferService } from '@app/modules/core/services/offer/offer.service';
import { RemoveReferalAction } from '@app/modules/core/states/app-state/app.actions';
import { AuthStateModel } from '@app/modules/core/states/auth-state/auth.model';
import { AuthState } from '@app/modules/core/states/auth-state/auth.state';
import { BookingData } from '@app/modules/core/states/booking-state/booking.model';
import { BookingState } from '@app/modules/core/states/booking-state/booking.state';
import { SetOfferAction } from '@app/modules/core/states/offer-state/offer.actions';
import { OfferStateModel } from '@app/modules/core/states/offer-state/offer.model';
import { OfferState } from '@app/modules/core/states/offer-state/offer.state';
import { TripStateModel } from '@app/modules/core/states/trip-state/trip.model';
import { TripState } from '@app/modules/core/states/trip-state/trip.state';
import { getOfferParams } from '@app/modules/core/utils/price-request';
import { NavTab } from '@shared/models';

import { getSpanishTimezoneUTC } from '../../const/date-picker.consts';
import { DateDialogComponent } from '../date-dialog/date-dialog.component';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'bkg-nav-tabs',
  templateUrl: './nav-tabs.component.html',
  host: { class: 'app-layout__steps' },
})
export class NavTabsComponent implements OnInit {
  @Input() navTabs: NavTab[];
  @Select(OfferState.offerData)
  offerData$: Observable<OfferStateModel>;
  @Select(BookingState.bookingData) bookingData$: Observable<BookingData>;
  @Select(AuthState.authData) authData$: Observable<AuthStateModel>;
  @Select(TripState.trip) tripData$: Observable<TripStateModel>;

  @Output() clickSummary = new EventEmitter<void>();

  offerData: OfferStateModel;
  bookingData: BookingData;
  tripData: TripStateModel;
  isBusinessMode = false;
  TIMEZONE_UTC_START: string;
  TIMEZONE_UTC_END: string;

  constructor(
    private store: Store,
    private dialog: DialogService,
    private googleTagManagerService: GoogleTagManagerService,
    private absuidService: AbsuidService,
    private offerService: OfferService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.offerData$
      .pipe(untilDestroyed(this))
      .subscribe((offerData: OfferStateModel) => {
        this.offerData = offerData;
      });

    this.bookingData$
      .pipe(untilDestroyed(this))
      .subscribe((bookingData: BookingData) => {
        this.bookingData = bookingData;
        this.TIMEZONE_UTC_START = getSpanishTimezoneUTC(
          this.bookingData.startDate
        );
        this.TIMEZONE_UTC_END = getSpanishTimezoneUTC(this.bookingData.endDate);
      });

    this.authData$
      .pipe(untilDestroyed(this))
      .subscribe((authData: AuthStateModel) => {
        this.isBusinessMode = !!authData.company;
      });

    this.tripData$
      .pipe(untilDestroyed(this))
      .subscribe((tripData: TripStateModel) => {
        this.tripData = tripData;
      });
  }

  getNavClass(): string {
    return this.isBusinessMode ? 'steps business' : 'steps';
  }
  getNavTabClass(navTab: NavTab): string {
    return navTab.class ? navTab.class : '';
  }

  onClickSummary(): void {
    this.clickSummary.emit();
  }

  openDateModal(): void {
    this.onModifyBookingData();
  }

  onModifyBookingData(): void {
    this.dialog
      .open(DateDialogComponent, {
        closeButton: false,
        size: 'sm',
        backdrop: true,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onSuccessModifyBookingData());
  }

  private onSuccessModifyBookingData(): void {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'cambio-fechas-reserva',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'cambio-fechas-reserva',
    });

    const offerParams = getOfferParams(
      this.offerData,
      this.absuidService,
      this.tripData,
    );
    this.calculateOffer(offerParams);
  }

  removeReferal(): void {
    this.store.dispatch(new RemoveReferalAction());
  }

  private calculateOffer(offerParams: PriceRequest): void {
    this.offerService
      .getOffer(offerParams)
      .pipe(untilDestroyed(this))
      .subscribe((offer: Offer) => {
        this.store.dispatch(new SetOfferAction(offer));
      });
  }

  get showSummaryButton() {
    const currentUrl = this.router.url;

    if (currentUrl.includes('/where-when') || currentUrl.includes('/confirmation')) return false;
    return true;
  }
}
